import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Menu, 
  MenuItem,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Grid,
  Paper,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select
} from '@mui/material';
import { 
  AccountCircle, 
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Call as CallIcon,
  PhoneForwarded as OutboundIcon,
  ExitToApp as LogoutIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Science as TestIcon,
  Feedback as FeedbackIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  SwapHoriz as SwitchIcon,
  BatchPrediction as BatchesIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { callsService, authService, organizationService } from '../utils/api';
import { COMPANY_LOGO, COMPANY_NAME } from '../config';

function Test() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Get user role for navigation
  const userRole = authService.getUserRole();
  const canAccessOutbound = userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator';
  
  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [assistantVariables, setAssistantVariables] = useState([]);
  
  // WhatsApp state
  const [whatsappConfigs, setWhatsappConfigs] = useState([]);
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState('');
  const [whatsappVariablesMapping, setWhatsappVariablesMapping] = useState({});
  const [whatsappContent, setWhatsappContent] = useState('');
  const [whatsappVariableValues, setWhatsappVariableValues] = useState({});
  const [callScheduleMinutes, setCallScheduleMinutes] = useState(30); // Default 30 minutes
  
  // Form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneType, setPhoneType] = useState('sip');
  const [countryCode, setCountryCode] = useState('+91');
  const [variableValues, setVariableValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  // Load organization data
  useEffect(() => {
    const org = organizationService.getSelectedOrganization();
    setCurrentOrganization(org);
    
    if (org) {
      // Set up phone type and country code from organization
      if (org.phone_types && org.phone_types.length > 0) {
        // Set default phone type from organization, or first available
        const defaultPhoneType = org.default_phone_type || org.phone_types[0];
        setPhoneType(defaultPhoneType);
      }
      
      if (org.country_codes && org.country_codes.length > 0) {
        // Set first country code as default
        setCountryCode(org.country_codes[0]);
      }
      
      // Set up assistant variables
      if (org.assistant_variables) {
        setAssistantVariables(org.assistant_variables);
        // Initialize assistant variable values
        const initialValues = {};
        org.assistant_variables.forEach(variable => {
          initialValues[variable] = '';
        });
        setVariableValues(initialValues);
      }
      
      // Set up WhatsApp configurations
      if (org.whatsapp_configs && org.whatsapp_configs.length > 0) {
        setWhatsappConfigs(org.whatsapp_configs);
        // Set the first template as default
        const firstTemplate = org.whatsapp_configs[0];
        setSelectedWhatsappTemplate(firstTemplate.name);
        setWhatsappVariablesMapping(firstTemplate.whatsapp_fields_mappings || {});
        
        // Initialize WhatsApp variable values using mapping keys
        const initialWhatsappValues = {};
        Object.keys(firstTemplate.whatsapp_fields_mappings || {}).forEach(key => {
          initialWhatsappValues[key] = '';
        });
        setWhatsappVariableValues(initialWhatsappValues);
      } else {
        // Legacy fallback for old schema
        if (org.whatsapp_variables_mapping && Object.keys(org.whatsapp_variables_mapping).length > 0) {
          setWhatsappVariablesMapping(org.whatsapp_variables_mapping);
          setWhatsappContent(org.whatsapp_content || '');
          // Initialize WhatsApp variable values using mapping keys
          const initialWhatsappValues = {};
          Object.keys(org.whatsapp_variables_mapping).forEach(key => {
            initialWhatsappValues[key] = '';
          });
          setWhatsappVariableValues(initialWhatsappValues);
        }
      }
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (phoneType === 'sip' && !/^\d{10}$/.test(phoneNumber)) {
      // SIP requires exactly 10 digits
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    } else if (phoneNumber.length < 7) {
      // Basic validation for other phone types
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVariableChange = (variable, value) => {
    setVariableValues(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  const handleWhatsappVariableChange = (variable, value) => {
    setWhatsappVariableValues(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  const handleTemplateChange = (event) => {
    const templateName = event.target.value;
    setSelectedWhatsappTemplate(templateName);
    
    // Find the selected template and update mapping
    const selectedTemplate = whatsappConfigs.find(config => config.name === templateName);
    if (selectedTemplate) {
      setWhatsappVariablesMapping(selectedTemplate.whatsapp_fields_mappings || {});
      
      // Reset WhatsApp variable values for new template
      const newWhatsappValues = {};
      Object.keys(selectedTemplate.whatsapp_fields_mappings || {}).forEach(key => {
        newWhatsappValues[key] = '';
      });
      setWhatsappVariableValues(newWhatsappValues);
    }
  };

  const handleCallScheduleChange = (e) => {
    const value = e.target.value;
    // Allow empty string for typing
    if (value === '') {
      setCallScheduleMinutes('');
      return;
    }
    
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      // Constrain to valid range
      const constrainedValue = Math.max(1, Math.min(1440, numValue));
      setCallScheduleMinutes(constrainedValue);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Send only the phone number digits (without +91 prefix)
      // The +91 is only for display purposes in the UI
      
      // Prepare assistant variables - only include non-empty values
      const assistantVariablesData = {};
      Object.keys(variableValues).forEach(key => {
        if (variableValues[key] && variableValues[key].trim() !== '') {
          assistantVariablesData[key] = variableValues[key].trim();
        }
      });
      
      // Prepare WhatsApp variables - include all mapping keys, even if empty
      const whatsappVariablesData = {};
      Object.keys(whatsappVariablesMapping).forEach(key => {
        whatsappVariablesData[key] = whatsappVariableValues[key] ? whatsappVariableValues[key].trim() : 'N/A';
      });
      
      const assistantVariablesToSend = Object.keys(assistantVariablesData).length > 0 ? assistantVariablesData : null;
      const whatsappVariablesToSend = Object.keys(whatsappVariablesMapping).length > 0 ? whatsappVariablesData : null;
      const hasWhatsappVars = Object.keys(whatsappVariablesMapping).length > 0;
      
      // Ensure schedule minutes is a valid number for API call
      const scheduleMinutes = callScheduleMinutes === '' ? 30 : callScheduleMinutes;
      
      await callsService.callTest(phoneNumber, assistantVariablesToSend, whatsappVariablesToSend, selectedWhatsappTemplate, scheduleMinutes, phoneType, countryCode);
      
      if (hasWhatsappVars) {
        setSuccess(`WhatsApp message sent! You'll receive a call in ${scheduleMinutes} minutes.`);
        toast.success(`WhatsApp message sent! Call scheduled for ${scheduleMinutes} minutes.`);
      } else {
        setSuccess("Great! You'll shortly receive a call on your number.");
        toast.success("Test call initiated successfully!");
      }
      
      // Reset form
      setPhoneNumber('');
      // Reset to organization defaults
      if (currentOrganization) {
        if (currentOrganization.phone_types && currentOrganization.phone_types.length > 0) {
          const defaultPhoneType = currentOrganization.default_phone_type || currentOrganization.phone_types[0];
          setPhoneType(defaultPhoneType);
        }
        if (currentOrganization.country_codes && currentOrganization.country_codes.length > 0) {
          setCountryCode(currentOrganization.country_codes[0]);
        }
      } else {
        setPhoneType('sip');
        setCountryCode('+91');
      }
      const resetValues = {};
      assistantVariables.forEach(variable => {
        resetValues[variable] = '';
      });
      setVariableValues(resetValues);
      
      const resetWhatsappValues = {};
      Object.keys(whatsappVariablesMapping).forEach(key => {
        resetWhatsappValues[key] = '';
      });
      setWhatsappVariableValues(resetWhatsappValues);
      setCallScheduleMinutes(30); // Reset to default 30 minutes
      
      setErrors({});
      
    } catch (error) {
      console.error('Test call error:', error);
      setError(error.message || 'Failed to initiate test call');
      toast.error(error.message || 'Failed to initiate test call');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = () => {
    navigate('/organizations');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Helper function to generate WhatsApp preview
  const getWhatsappPreview = () => {
    if (!whatsappContent) return '';
    let preview = whatsappContent;
    // Use the field names from mapping for preview replacement
    Object.entries(whatsappVariablesMapping).forEach(([key, fieldName]) => {
      const value = whatsappVariableValues[key] || `[${fieldName}]`;
      preview = preview.replace(new RegExp(`\\$\\$${fieldName}`, 'g'), value);
    });
    return preview;
  };

  // Determine if we have WhatsApp variables
  const hasWhatsappVariables = whatsappConfigs.length > 0 || Object.keys(whatsappVariablesMapping).length > 0;

  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Call Logs', icon: <CallIcon />, path: '/call-logs' },
    ...(canAccessOutbound && currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Outbound Calls', icon: <OutboundIcon />, path: '/outbound' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Batches', icon: <BatchesIcon />, path: '/batches' }] : []),
    ...(userRole !== 'viewer' ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    ...(userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator' ? [{ text: 'Feedback', icon: <FeedbackIcon />, path: '/feedback' }] : []),
    ...(currentOrganization?.call_type !== 'inboundPhoneCall' ? [{ text: 'Test', icon: <TestIcon />, path: '/test' }] : []),
  ];

  const drawerWidth = 240;

  const drawer = (
    <Box sx={{ 
      width: drawerWidth, 
      height: '100%',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fc 100%)',
      borderRight: '1px solid rgba(102, 117, 140, 0.12)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        pt: '12px',
        pb: '12px',
        px: 2,
        borderBottom: '1px solid rgba(102, 117, 140, 0.08)',
        background: '#2541b2',
        color: 'white',
        position: 'relative'
      }}>
        {/* Side-by-side logos */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          justifyContent: 'center',
          width: '100%'
        }}>
          <Box
            component="img"
            src="/telly_logo.png"
            alt="TellyFlow"
            sx={{
              width: 'auto',
              height: '32px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1)'
            }}
          />
          <Box sx={{ 
            width: '1px', 
            height: '24px', 
            backgroundColor: 'rgba(255, 255, 255, 0.3)' 
          }} />
          <Box
            sx={{
              width: '60px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {COMPANY_LOGO && (
              <Box
                component="img"
                src={COMPANY_LOGO}
                alt={`${COMPANY_NAME || 'Company'} Logo`}
                sx={{
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 2 }}>
          {drawerItems.map((item) => {
            const isActive = item.text === 'Test';
            return (
              <ListItem 
                button 
                key={item.text} 
                onClick={() => navigate(item.path)}
                sx={{ 
                  mb: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: isActive ? 'rgba(67, 97, 238, 0.08)' : 'transparent',
                  border: isActive ? '1px solid rgba(67, 97, 238, 0.2)' : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(67, 97, 238, 0.12)' : 'rgba(102, 117, 140, 0.04)',
                    transform: 'translateX(2px)',
                    boxShadow: isActive ? '0 4px 12px rgba(67, 97, 238, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                  },
                  '&:active': {
                    transform: 'translateX(1px)'
                  }
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 40,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{ 
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'all 0.2s ease-in-out'
                  }} 
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      ml: 1
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid rgba(102, 117, 140, 0.08)',
        background: 'rgba(102, 117, 140, 0.02)'
      }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(239, 71, 111, 0.08)',
              transform: 'translateX(2px)',
              boxShadow: '0 2px 8px rgba(239, 71, 111, 0.12)'
            },
            '&:active': {
              transform: 'translateX(1px)'
            }
          }}
        >
          <ListItemIcon 
            sx={{ 
              minWidth: 40,
              color: 'text.secondary',
              transition: 'color 0.2s ease-in-out',
              '.MuiListItem-root:hover &': {
                color: 'error.main'
              }
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out'
            }} 
          />
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Sidebar drawer */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${drawerOpen && !isMobile ? drawerWidth : 0}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="transparent" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Test Call
            </Typography>
            
            {/* Organization Switcher Button */}
            {currentOrganization && (
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                endIcon={<SwitchIcon />}
                onClick={handleSwitchOrganization}
                sx={{
                  mr: 2,
                  borderColor: 'rgba(67, 97, 238, 0.3)',
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(67, 97, 238, 0.05)',
                    color: 'primary.main'
                  }
                }}
              >
                {currentOrganization.name}
              </Button>
            )}
            
            <IconButton color="inherit" onClick={handleOpenMenu}>
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: 'text.primary' }}>
            Test Call
          </Typography>

          {/* Organization Info */}
          {currentOrganization && (
            <Card sx={{ borderRadius: 3, boxShadow: 1, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {currentOrganization.name}
                  </Typography>
                  <Chip 
                    label={currentOrganization.assistant_name || 'Assistant'} 
                    color="primary" 
                    variant="outlined" 
                    size="small" 
                  />
                </Box>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={3}>
            {/* Phone Number and Submit */}
            <Grid item xs={12} md={hasWhatsappVariables ? 4 : 6}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                    Test Your Phone Number
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter your mobile number to receive a test call.
                  </Typography>
                  
                  {success && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      {success}
                    </Alert>
                  )}
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        value={phoneNumber}
                        onChange={(e) => {
                          // Only allow digits and limit to 10 characters
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setPhoneNumber(value);
                        }}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber || "Enter mobile number"}
                        disabled={loading}
                        placeholder="9876543210"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon color="primary" />
                                {currentOrganization?.country_codes && currentOrganization.country_codes.length > 0 ? (
                                  <FormControl variant="standard" sx={{ minWidth: 80 }}>
                                    <Select
                                      value={countryCode}
                                      onChange={(e) => setCountryCode(e.target.value)}
                                      disabled={loading}
                                      sx={{
                                        '&:before': { display: 'none' },
                                        '&:after': { display: 'none' },
                                        '& .MuiSelect-select': {
                                          py: 0.5,
                                          px: 1,
                                          fontSize: '0.875rem',
                                          fontWeight: 500
                                        }
                                      }}
                                    >
                                      {currentOrganization.country_codes.map((code) => (
                                        <MenuItem key={code} value={code}>
                                          {code}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <Typography variant="body1" color="text.secondary">
                                    {countryCode}
                                  </Typography>
                                )}
                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                              </Box>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Phone Type Selection */}
                    {currentOrganization?.phone_types && currentOrganization.phone_types.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                            Phone Type:
                          </FormLabel>
                          <RadioGroup
                            value={phoneType}
                            onChange={(e) => setPhoneType(e.target.value)}
                            row
                          >
                            {currentOrganization.phone_types.map((type) => (
                              <FormControlLabel
                                key={type}
                                value={type}
                                control={<Radio size="small" />}
                                label={type.charAt(0).toUpperCase() + type.slice(1)}
                                disabled={loading}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    )}

                    {/* Call Schedule Time Input - only show when WhatsApp variables exist */}
                    {hasWhatsappVariables && (
                      <Box sx={{ mb: 3 }}>
                        <TextField
                          label="Call Schedule Time"
                          variant="outlined"
                          fullWidth
                          type="number"
                          value={callScheduleMinutes}
                          onChange={handleCallScheduleChange}
                          disabled={loading}
                          placeholder="30"
                          helperText="Minutes to wait before making the call (1-1440 minutes)"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                          }}
                          inputProps={{
                            min: 1,
                            max: 1440,
                            step: 1
                          }}
                        />
                      </Box>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <CallIcon />}
                      sx={{ 
                        py: 1.5,
                        px: 4,
                        fontWeight: 500,
                        width: '100%'
                      }}
                    >
                      {hasWhatsappVariables 
                        ? (loading ? 'Sending WhatsApp...' : `Send WhatsApp & Call After ${callScheduleMinutes || 30} mins`)
                        : (loading ? 'Initiating Call...' : 'Call Now')
                      }
                    </Button>
                  </form>

                  <Box sx={{ mt: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>How it works:</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      • Enter your 10-digit mobile number in the field above
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      • Fill out any variables (optional)
                    </Typography>
                    {hasWhatsappVariables ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          • Click the button to send WhatsApp message immediately
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • You'll receive a call {callScheduleMinutes || 30} minutes after the WhatsApp message
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          • Click "Call Now" to initiate the call immediately
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • You'll receive a call within a few seconds to test the system
                        </Typography>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Assistant Variables */}
            {assistantVariables.length > 0 && (
              <Grid item xs={12} md={hasWhatsappVariables ? 4 : 6}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <SettingsIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        Assistant Variables
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Configure values for your assistant variables (optional).
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {assistantVariables.map((variable, index) => (
                        <TextField
                          key={variable}
                          label={variable.charAt(0).toUpperCase() + variable.slice(1)}
                          variant="outlined"
                          fullWidth
                          value={variableValues[variable] || ''}
                          onChange={(e) => handleVariableChange(variable, e.target.value)}
                          disabled={loading}
                          placeholder={`Enter ${variable}`}
                          helperText={`Optional: Value for ${variable} variable`}
                        />
                      ))}
                    </Box>
                    
                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.200' }}>
                      <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                        <strong>Note:</strong>
                      </Typography>
                      <Typography variant="body2" color="info.main">
                        These variables will be passed to your assistant during the call. Leave empty if not needed.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* WhatsApp Variables - only shown if hasWhatsappVariables */}
            {hasWhatsappVariables && (
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <PhoneIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        WhatsApp Message
                      </Typography>
                    </Box>
                    
                    {/* Template Selection - only show if multiple templates available */}
                    {whatsappConfigs.length > 1 && (
                      <Box sx={{ mb: 3 }}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                            Select Template:
                          </FormLabel>
                          <RadioGroup
                            value={selectedWhatsappTemplate}
                            onChange={handleTemplateChange}
                            row
                          >
                            {whatsappConfigs.map((config) => (
                              <FormControlLabel
                                key={config.name}
                                value={config.name}
                                control={<Radio size="small" />}
                                label={config.name}
                                disabled={loading}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    )}
                    
                    {/* Show selected template name if only one template */}
                    {whatsappConfigs.length === 1 && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                          Template: {whatsappConfigs[0].name}
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Fill in the variables to customize your WhatsApp message.
                    </Typography>
                    
                    {/* WhatsApp variable inputs */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {Object.entries(whatsappVariablesMapping).map(([key, fieldName]) => (
                        <TextField
                          key={key}
                          label={fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace('_', ' ')}
                          variant="outlined"
                          fullWidth
                          value={whatsappVariableValues[key] || ''}
                          onChange={(e) => handleWhatsappVariableChange(key, e.target.value)}
                          disabled={loading}
                          placeholder={`Enter ${fieldName.replace('_', ' ')}`}
                          helperText={`Value for WhatsApp message variable: ${fieldName.replace('_', ' ')}`}
                        />
                      ))}
                      
                    </Box>
                    
                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
                      <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                        <strong>WhatsApp Flow:</strong>
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        Message sent immediately, then call scheduled for {callScheduleMinutes || 30} minutes later.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Test; 